import { Button, Form, Input, Select, ConfigProvider, message, Modal, Checkbox, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/zh-cn';
import './App.css'
import { SolarDay, SolarTime } from 'tyme4ts';
import { Solar } from 'lunar-typescript';
import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import FourPillars from './components/FourPillars';
import AILoadingAnimation from './components/AILoadingAnimation';
import BirthDateTimePicker from './components/BirthDateTimePicker';
import { CITY_OPTIONS, cityFilterOption, type City } from './data/cities';
import { calculateDaYunWithAge, type DaYunWithAge, ZIWEI_CONFIG, buildZiWeiData } from './utils';
import Report from './pages/Report';
import { readReportStream } from './utils/report-stream';
import { useIztro } from 'iztro-hook';
import CustomerServiceFloat from './components/CustomerServiceFloat';
import weixinQR from './assets/weixin.jpg';
dayjs.locale('zh-cn');
dayjs.extend(dayOfYear);

// 常量定义
const DEFAULT_ZIWEI_BIRTHDAY = '2000-1-1';

interface FreeSection {
  key: string;
  badge: string;
  title: string;
  description: string;
  tags: string[];
  href: string;
  themeClassName: string;
}

// 成长与方向专题入口（通过 ?mode=full 参数访问）
const FREE_SECTIONS: FreeSection[] = [
  {
    key: 'talent-kid',
    badge: '家长高频',
    title: '看孩子天赋',
    description: '从先天气质、兴趣表现、学习驱动力与表达模式出发，帮助家长更早看见孩子的优势方向。',
    tags: ['学习潜能', '天赋倾向', '培养建议', '亲子沟通'],
    href: '/talent-kid',
    themeClassName: 'home-extension-card-kid',
  },
  {
    key: 'career',
    badge: '决策专题',
    title: '职业选择分析',
    description: '适合正处在转行、选岗、升职、创业犹豫期的人，梳理适配赛道、发力周期与避坑重点。',
    tags: ['职业定位', '赛道匹配', '财富节奏', '决策参考'],
    href: '/career',
    themeClassName: 'home-extension-card-career',
  },
  {
    key: 'marriage',
    badge: '情感专题',
    title: '婚姻专题分析',
    description: '重点看缘分出现方式、成婚时机、婚后相处底色与风险点，适合单身、恋爱中、已婚复盘的人。',
    tags: ['正缘时机', '婚姻质量', '风险排查', '经营建议'],
    href: '/marriage',
    themeClassName: 'home-extension-card-marriage',
  },
];

// 检测是否为完整模式（?mode=full）
const isFullMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'full';
};

interface FormValues {
  birthDateTime: {
    date: dayjs.Dayjs;
    isLunar: boolean;
  };
  gender: 'male' | 'female';
  city?: string;
  longitude: number;
  distinguishZiShi?: boolean;
}

type RequestSource = '邀请码验证' | '邀请码扣次' | '毒舌版接口' | '温和版接口';

class RequestError extends Error {
  source: RequestSource;
  status?: number;
  details?: string;

  constructor(source: RequestSource, message: string, status?: number, details?: string) {
    super(message);
    this.name = 'RequestError';
    this.source = source;
    this.status = status;
    this.details = details;
  }
}

async function readApiPayload(resp: Response) {
  const contentType = resp.headers.get('Content-Type') || '';

  if (contentType.includes('application/json')) {
    return await resp.json();
  }

  const text = await resp.text();
  return text ? { error: text } : {};
}

async function assertApiSuccess(resp: Response, source: RequestSource) {
  if (resp.ok) {
    return await readApiPayload(resp);
  }

  const payload = await readApiPayload(resp);
  const message =
    payload?.error ||
    payload?.message ||
    `${source}失败`;
  const details = payload?.details || payload?.message;

  throw new RequestError(source, message, resp.status, details);
}

function formatRequestError(error: unknown, fallbackSource: RequestSource) {
  const sourceActionMap: Record<RequestSource, string> = {
    '邀请码验证': '请检查邀请码是否正确，或联系客服确认邀请码状态',
    '邀请码扣次': '报告可能已生成，但邀请码状态回写失败，请联系客服处理',
    '毒舌版接口': '这是毒舌版报告生成接口异常，建议稍后重试；若多次失败，优先切换温和版验证是否为毒舌版负载问题',
    '温和版接口': '这是温和版报告生成接口异常，建议稍后重试',
  };

  if (error instanceof RequestError) {
    const suffix = error.details && error.details !== error.message ? `：${error.details}` : '';
    return [
      `【${error.source}出错】`,
      `${error.message}${suffix}`,
      sourceActionMap[error.source],
    ].join(' ');
  }

  if (!navigator.onLine) {
    return '网络连接失败，请检查网络后重试';
  }

  if (error instanceof TypeError && error.message?.includes('fetch')) {
    return `【${fallbackSource}出错】网络请求异常，请检查网络连接后重试。`;
  }

  if (error instanceof Error && error.message) {
    return `【${fallbackSource}出错】${error.message} ${sourceActionMap[fallbackSource]}`;
  }

  return `【${fallbackSource}出错】请稍后再试。${sourceActionMap[fallbackSource]}`;
}

// 将标准时间转换为真太阳时（支持分钟精度）
function toTrueSolarTimeWithMinutes(datetime: dayjs.Dayjs, longitude: number, timeZoneOffset: number = 8): dayjs.Dayjs {
  const n = datetime.dayOfYear();
  const B = (2 * Math.PI * (n - 81)) / 364;
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const zoneLongitude = 15 * timeZoneOffset;
  const deltaLon = 4 * (longitude - zoneLongitude);
  const deltaMinutes = eot + deltaLon;
  return datetime.add(deltaMinutes, 'minute');
}

// 将 24 小时制转换为关系结构模型时辰索引 (0-12)
function hourToShiChenIndex(hour: number): number {
  return Math.floor((hour + 1) / 2);
}

function App() {
  const [showReport, setShowReport] = React.useState(false);
  const [form] = Form.useForm();
  
  // ========== 公告栏状态 ==========
  const [announcementVisible, setAnnouncementVisible] = React.useState(() => {
    // 检查本地存储，如果用户已关闭则不再显示
    const savedState = localStorage.getItem('announcement_closed');
    const envShow = import.meta.env.VITE_ANNOUNCEMENT_SHOW;
    // 如果环境变量设置为 false，或用户已关闭，则不显示
    if (savedState === 'true' || envShow === 'false') {
      return false;
    }
    return true;
  });
  
  // 关闭公告
  const closeAnnouncement = () => {
    setAnnouncementVisible(false);
    localStorage.setItem('announcement_closed', 'true');
  };
  
  // ========== 邀请码相关状态 ==========
  // 毒舌版专用邀请码
  const [toxicInviteCode, setToxicInviteCode] = React.useState<string>('');
  const [isToxicUnlocked, setIsToxicUnlocked] = React.useState(false);
  const [toxicRemainingUses, setToxicRemainingUses] = React.useState<number>(0);
  const [showToxicInviteInput, setShowToxicInviteInput] = React.useState(false);
  
  // ========== 客服弹窗状态 ==========
  const [isServiceModalVisible, setIsServiceModalVisible] = React.useState(false);
  
  // ========== 行为模型相关状态 ==========
  const [fourPillars, setFourPillars] = React.useState<{ year: string, month: string, day: string, hour: string } | null>(null);
  const [daYun, setDaYun] = React.useState<DaYunWithAge[]>([]);
  const [lunarDateStr, setLunarDateStr] = React.useState<string>('');
  const [trueSolarTimeStr, setTrueSolarTimeStr] = React.useState<string>(''); // 真太阳时字符串
  
  // ========== AI 相关状态 ==========
  const [aiLoading, setAiLoading] = React.useState(false);
  const [reportGenerating, setReportGenerating] = React.useState(false);
  const [reportError, setReportError] = React.useState('');
  const [aiAnswer, setAiAnswer] = React.useState<string | null>(null);
  // const [reportId, setReportId] = React.useState<string>(''); // 暂时不用
  const [isLocked, setIsLocked] = React.useState(false);
  const [reportStyle, setReportStyle] = React.useState<'normal' | 'toxic'>('normal');

  // ========== 页面初始化：恢复表单草稿 ==========
  React.useEffect(() => {
    // 暂时注释，不加载 URL 中的 reportId
    /*
    const params = new URLSearchParams(window.location.search);
    const id = params.get('reportId');
    
    if (id) {
      // 场景1：用户从分享链接回来，加载历史报告
      const fetchSavedReport = async () => {
        try {
          setAiLoading(true);
          const resp = await fetch(`/api/report?id=${id}`);
          if (resp.ok) {
            const data = await resp.json();
            console.log('data', data)
            setAiAnswer(data.content);
            setIsLocked(data.isLocked || false);
            setReportId(id);
            setShowReport(true);
            
            // 如果有出生信息，恢复排盘数据
            if (data.birthInfo) {
              form.setFieldsValue(data.birthInfo);
              
              // 调用排盘函数恢复行为模型、阶段趋势、关系结构分析图谱
              performPaiPan({
                ...data.birthInfo,
                gender: data.birthInfo.gender || 'male'  // 默认男性
              });
              
              message.success('欢迎回来！已为您恢复上次的测算记录');
            }
          }
        } catch (e) {
          console.error('加载保存的报告失败:', e);
        } finally {
          setAiLoading(false);
        }
      };
      fetchSavedReport();
    } else {
    */
    
    // 场景：全新访问，尝试恢复表单草稿
    const savedDraft = localStorage.getItem('bazi_form_draft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        form.setFieldsValue(draftData);
        message.info({
          content: '💾 检测到上次未完成的输入，已为您恢复',
          duration: 3
        });
      } catch (e) {
        console.error('恢复表单草稿失败:', e);
      }
    }
    
    // } // 注释结束
  }, []);
  
  // ========== 表单字段监听：实时保存到 localStorage ==========
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    // 只在有有效数据时保存（至少填了年份）
    if (currentValues.year) {
      localStorage.setItem('bazi_form_draft', JSON.stringify(currentValues));
    }
  };
  
  // ========== 关系结构模型相关状态 ==========
  const [ziWeiBirthday, setZiWeiBirthday] = React.useState<string>('');
  const [ziWeiBirthTime, setZiWeiBirthTime] = React.useState<number>(0);
  const [ziWeiGender, setZiWeiGender] = React.useState<'男' | '女'>('男');
  const [horoscopeDate, setHoroscopeDate] = React.useState<string | Date>(new Date());
  const [horoscopeHour, setHoroscopeHour] = React.useState<number>(0);
  
  // 使用 useIztro Hook（按照中州派三合理论配置）
  const { astrolabe, setHoroscope } = useIztro({
    birthday: ziWeiBirthday || DEFAULT_ZIWEI_BIRTHDAY,
    birthTime: ziWeiBirthTime,
    gender: ziWeiGender,
    birthdayType: 'solar',
    fixLeap: true,
    lang: 'zh-CN',
    // ========== 专业派别配置（中州派 + 三合派）==========
    ...(ZIWEI_CONFIG as any)
  });
  // ========== 构建关系结构模型数据（使用公共函数）==========
  const ziWeiData = useMemo(() => {
    if (!astrolabe || !ziWeiBirthday || ziWeiBirthday === DEFAULT_ZIWEI_BIRTHDAY) {
      return null;
    }
    return buildZiWeiData(astrolabe, ziWeiBirthTime);
  }, [astrolabe, ziWeiBirthday, ziWeiBirthTime]);

  // ========== 更新趋势数据（仅在有效数据时） ==========
  useEffect(() => {
    if (ziWeiBirthday && ziWeiBirthday !== DEFAULT_ZIWEI_BIRTHDAY && horoscopeDate && horoscopeHour !== undefined && setHoroscope) {
      setHoroscope(horoscopeDate, horoscopeHour);
    }
  }, [horoscopeDate, horoscopeHour, setHoroscope, ziWeiBirthday]);

  // ========== 构建城市选项 ==========
  const cityOptions = React.useMemo(() => CITY_OPTIONS, []);

  // ========== 事件处理函数 ==========
  // 处理城市选择
  const handleCityChange = (_cityName: string, option: any) => {
    const selectedCity = option.city as City;
    form.setFieldsValue({
      longitude: selectedCity.longitude
    });
  };
  
  // ===== 排盘逻辑：计算行为模型、阶段趋势、关系结构模型 =====
  const performPaiPan = (birthInfo: { 
    year?: number; 
    month?: number; 
    day?: number; 
    hour?: number; 
    minute?: number;
    solar?: string;  // 支持 "1997-04-27 01:30" 格式
    longitude: number;
    gender: 'male' | 'female';
    distinguishZiShi?: boolean; // 是否区分早晚子时
  }) => {
    let inputYear: number, inputMonth: number, inputDay: number, inputHour: number, inputMinute: number;
    
    // 支持两种格式：1. 分离字段  2. solar 字符串
    if (birthInfo.solar) {
      // 从 solar 字符串解析："1997-04-27 01:30"
      const solarDate = dayjs(birthInfo.solar);
      inputYear = solarDate.year();
      inputMonth = solarDate.month() + 1;
      inputDay = solarDate.date();
      inputHour = solarDate.hour();
      inputMinute = solarDate.minute();
    } else {
      // 使用分离字段
      inputYear = birthInfo.year!;
      inputMonth = birthInfo.month!;
      inputDay = birthInfo.day!;
      inputHour = birthInfo.hour!;
      inputMinute = birthInfo.minute!;
    }
    
    const { longitude, gender, distinguishZiShi = false } = birthInfo;
      
    // ===== 第一步：计算真太阳时 =====
    const standard = dayjs(`${inputYear}-${inputMonth.toString().padStart(2, '0')}-${inputDay.toString().padStart(2, '0')} ${inputHour.toString().padStart(2, '0')}:${inputMinute.toString().padStart(2, '0')}:00`);
    const trueSolar = toTrueSolarTimeWithMinutes(standard, longitude, 8);
  
    // 标准时间参数
    let standardYear = standard.year();
    let standardMonth = standard.month() + 1;
    let standardDay = standard.date();
    const standardHour = standard.hour();
    const standardMinute = standard.minute();
    
    // ===== 子时处理逻辑 =====
    // 判断是否在子时范围（23:00-01:00）
    const isInZiShi = standardHour === 23 || standardHour === 0;
    
    if (isInZiShi) {
      if (distinguishZiShi) {
        // 现代计法：区分早晚子时
        if (standardHour === 23) {
          // 早子时（23:00-00:00）：算当天，不调整日期
          console.log('🕐 早子时（23:00-24:00），日柱使用当天');
        } else if (standardHour === 0) {
          // 晚子时（00:00-01:00）：算第二天，日期已经是第二天了，无需调整
          console.log('🕐 晚子时（00:00-01:00），日柱使用第二天');
        }
      } else {
        // 传统计法：23:00-01:00 统一算第二天
        if (standardHour === 23) {
          // 23:00-24:00：需要将日期推进到第二天
          const nextDay = standard.add(1, 'day');
          standardYear = nextDay.year();
          standardMonth = nextDay.month() + 1;
          standardDay = nextDay.date();
          console.log('🕐 传统子时（23:00-24:00），日柱推进到第二天:', `${standardYear}-${standardMonth}-${standardDay}`);
        } else {
          // 00:00-01:00：已经是第二天了，无需调整
          console.log('🕐 传统子时（00:00-01:00），日柱已是第二天');
        }
      }
    }
  
    // 真太阳时参数
    const year = trueSolar.year();
    const month = trueSolar.month() + 1;
    const day = trueSolar.date();
    const trueSolarHour = trueSolar.hour();
    const trueSolarMinute = trueSolar.minute();
    
    // 保存真太阳时字符串用于展示
    setTrueSolarTimeStr(trueSolar.format('YYYY年MM月DD日 HH:mm'));
  
    // ===== 第二步：计算行为模型四柱 =====
    // 重要：年柱、月柱、日柱必须基于北京时间（标准时）的日期
    // 只有时柱才使用真太阳时修正
    const solarDayStandard = SolarDay.fromYmd(standardYear, standardMonth, standardDay);
    const lunarDayStandard = solarDayStandard.getLunarDay();
    const solarObj = Solar.fromDate(standard.toDate());
    const lunarObj = solarObj.getLunar();
    
    // 时柱使用真太阳时修正后的时间
    const solarHourTrue = SolarTime.fromYmdHms(year, month, day, trueSolarHour, trueSolarMinute, 0);
    const lunarHourTrue = solarHourTrue.getLunarHour();
      
    // 年柱按立春时刻精确切换
    const sixtyCycleYear = lunarObj.getYearInGanZhiExact();
    let sixtyCycleMonth = lunarDayStandard.getMonthSixtyCycle().getName();
    const sixtyCycleDay = lunarDayStandard.getSixtyCycle().getName();
    const sixtyCycleHour = lunarHourTrue.getSixtyCycle().getName();
    
    // ===== 修正月柱：lunar-typescript 不支持精确到时分的节气判断 =====
    // 需要手动检查当天是否为节气日，并判断出生时间是否在节气时刻之前
    try {
      const currentJieQi = lunarObj.getCurrentJieQi();
      
      if (currentJieQi) {
        const jieQiSolar = currentJieQi.getSolar();
        // 判断是否为节日（非中气）
        if (currentJieQi.isJie()) {
          // 比较出生时间与节气时刻
          const birthTimestamp = standard.valueOf();
          const jieQiTimestamp = new Date(
            jieQiSolar.getYear(),
            jieQiSolar.getMonth() - 1,
            jieQiSolar.getDay(),
            jieQiSolar.getHour(),
            jieQiSolar.getMinute(),
            jieQiSolar.getSecond()
          ).getTime();
          
          // 如果出生在节气时刻之前，使用上一个月柱
          if (birthTimestamp < jieQiTimestamp) {
            const prevJieQi = lunarObj.getPrevJieQi();
            if (prevJieQi) {
              // 获取前一个节气对应的日期（节气当天）
              const prevJieQiSolar = prevJieQi.getSolar();
              const prevSolar = Solar.fromYmd(
                prevJieQiSolar.getYear(),
                prevJieQiSolar.getMonth(),
                prevJieQiSolar.getDay()
              );
              sixtyCycleMonth = prevSolar.getLunar().getMonthInGanZhi();
              console.log(`📅 出生在节气 ${currentJieQi.getName()} 之前，月柱修正为: ${sixtyCycleMonth}`);
            }
          }
        }
      }
    } catch (error) {
      console.error('⚠️ 月柱节气精确判断失败，使用默认值:', error);
    }
  
    // 计算农历显示字符串
    setLunarDateStr(`${lunarObj.getYearInGanZhiExact()}(${lunarObj.getYearShengXiaoExact()})年 ${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()} ${lunarHourTrue.getSixtyCycle().getName()}时`);
  
    setFourPillars({ year: sixtyCycleYear, month: sixtyCycleMonth, day: sixtyCycleDay, hour: sixtyCycleHour });
      
    // ===== 第三步：计算阶段趋势 =====
    const daYunList = calculateDaYunWithAge(standardYear, standardMonth, standardDay, standardHour, standardMinute, sixtyCycleYear, sixtyCycleMonth, gender, 8);
    setDaYun(daYunList);
  
    // ===== 第四步：计算关系结构模型 =====
    try {
      // 处理小时数：确保在 0-23 范围内
      let validHour = Math.floor(trueSolarHour);
      if (validHour < 0) validHour = 0;
      if (validHour > 23) validHour = 23;

      // 格式化日期为标准格式 YYYY-MM-DD
      const birthdayStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
      // 转换为时辰索引 (0-12)
      const shiChenIndex = hourToShiChenIndex(validHour);
        
      // 设置关系结构模型参数
      setZiWeiBirthday(birthdayStr);
      setZiWeiBirthTime(shiChenIndex);
      setZiWeiGender(gender === 'male' ? '男' : '女');
      setHoroscopeDate(new Date());
      setHoroscopeHour(shiChenIndex);
    } catch (error) {
      console.error('设置关系结构模型参数失败', error);
      message.warning('关系结构模型计算失败，仅显示行为模型信息');
    }
  };
  
  // 开始排盘
  const onFinish = async (values: FormValues) => {
    const { birthDateTime, longitude, gender, distinguishZiShi } = values;
    if (!birthDateTime) {
      message.error('请选择出生日期和时间');
      return;
    }
    const { date } = birthDateTime;
    const longitudeNum = Number(longitude);

    // 参数验证
    if (Number.isNaN(longitudeNum)) {
      message.error('请输入有效的经度，例如 116.4');
      return;
    }

    // ===== 验证邀请码（在排盘前验证） =====
    // 暂时注释，不使用邀请码功能
    // if (inviteCode.trim() && !isCodeVerified) {
    //   const hide = message.loading('正在验证邀请码...', 0);
    //   const verifySuccess = await handleVerifyInviteCode(inviteCode);
    //   hide();
    //   
    //   if (!verifySuccess) {
    //     return; // 验证失败，不继续排盘
    //   }
    // }

    // ===== 获取标准时间 =====
    const standard = date.second(0).millisecond(0);

    // ===== 调用排盘函数 =====
    performPaiPan({
      year: standard.year(),
      month: standard.month() + 1,
      day: standard.date(),
      hour: standard.hour(),
      minute: standard.minute(),
      longitude: longitudeNum,
      gender,
      distinguishZiShi: distinguishZiShi || false
    });
  };

  // 验证毒舌版邀请码
  const handleVerifyToxicCode = async (code: string): Promise<boolean> => {
    if (!code.trim() || code.length < 4) {
      message.error('邀请码太短，至少4位');
      return false;
    }

    try {
      const resp = await fetch('/api/invite-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'verify',
          code: code.trim()
        })
      });

      const data = await assertApiSuccess(resp, '邀请码验证');
      
      setIsToxicUnlocked(true);
      setToxicRemainingUses(data.remaining);
      message.success(data.message || '解锁成功！');
      return true;
    } catch (error) {
      console.error('验证邀请码失败:', error);
      setIsToxicUnlocked(false);
      message.error(formatRequestError(error, '邀请码验证'));
      return false;
    }
  };

  // 消耗毒舌版邀请码使用次数
  const consumeToxicCode = async (): Promise<boolean> => {
    if (!toxicInviteCode.trim()) return false;
    
    try {
      const resp = await fetch('/api/invite-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'consume',
          code: toxicInviteCode.trim()
        })
      });

      const data = await assertApiSuccess(resp, '邀请码扣次');
      
      setToxicRemainingUses(data.remaining);
      return true;
    } catch (error) {
      console.error('消耗邀请码失败:', error);
      message.error(formatRequestError(error, '邀请码扣次'));
      return false;
    }
  };

  // 邀请码输入监听
  // 暂时注释，不使用邀请码功能
  /*
  const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const code = e.target.value.toUpperCase();
    setInviteCode(code);
    
    // 清除之前的验证状态
    if (isCodeVerified) {
      setIsCodeVerified(false);
    }
  };

  // 消耗一次邀请码使用次数
  const consumeInviteCode = async () => {
    try {
      const resp = await fetch('/api/invite-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'consume',
          code: inviteCode.trim()
        })
      });

      const data = await resp.json();
      
      if (resp.ok) {
        setRemainingUses(data.remaining);
        return true;
      } else {
        message.error(data.error || '邀请码已用完');
        return false;
      }
    } catch (error) {
      console.error('消耗邀请码失败:', error);
      return false;
    }
  };
  */

  // AI 解读分析图谱
  const handleAiInterpret = async () => {
    // 1. 检查排盘数据
    if (!fourPillars || daYun.length === 0) {
      message.error('请先完成排盘，再使用解读');
      return;
    }

    // 2. 检查毒舌版邀请码 - 只验证，不消耗
    if (reportStyle === 'toxic') {
      // 如果尚未解锁，先验证邀请码
      if (!isToxicUnlocked) {
        if (!toxicInviteCode.trim()) {
          message.error('请输入邀请码');
          return;
        }
        
        const hide = message.loading('正在验证邀请码...', 0);
        const verifySuccess = await handleVerifyToxicCode(toxicInviteCode);
        hide();
        
        if (!verifySuccess) {
          return; // 验证失败，不继续
        }
      }
      
      // ⚠️ 注意：这里不再消耗邀请码，改为在 API 成功后消耗
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, userQuestion } = values;

    if (!birthDateTime) {
      message.error('请先填写出生日期和时间');
      return;
    }

    const { date, isLunar } = birthDateTime;

    const birthInfo = {
      solar: date.format('YYYY-MM-DD HH:mm'),
      lunar: lunarDateStr,
      isLunarInput: isLunar,
      city,
      longitude,
      gender: gender || 'male'
    };

    // 检查关系结构模型数据
    if (!ziWeiData) {
      message.warning('关系结构模型数据尚未计算完成，请稍后再试或仅使用行为模型数据');
    }

    setAiLoading(true);
    setReportGenerating(true);
    setReportError('');
    setAiAnswer(null);
    const requestStartedAt = performance.now();
    let debugRequestId: string | null = null;
    try {
      const requestSource: RequestSource = reportStyle === 'toxic' ? '毒舌版接口' : '温和版接口';
      const resp = await fetch('/api/bazi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          fourPillars,
          daYun,
          gender,
          birthInfo,
          ziWeiData, // 添加关系结构模型数据
          question: userQuestion || '', // 传入用户问题
          type: reportStyle, // 新增参数：报告风格
        }),
      });

      debugRequestId = resp.headers.get('X-Debug-Request-Id');
      console.info('[bazi] API response headers received', {
        requestId: debugRequestId,
        status: resp.status,
        headersMs: Math.round(performance.now() - requestStartedAt),
        serverTiming: resp.headers.get('Server-Timing'),
      });

      if (!resp.ok) {
        await assertApiSuccess(resp, requestSource);
      }

      const data = resp.headers.get('Content-Type')?.includes('text/event-stream') && resp.body
        ? await readReportStream(resp.body, (answer) => {
          setAiAnswer(answer);
          setIsLocked(false);
          setShowReport(true);
          setAiLoading(false);
        })
        : await readApiPayload(resp);
      console.info('[bazi] API response body parsed', {
        requestId: debugRequestId || data.requestId,
        totalMs: Math.round(performance.now() - requestStartedAt),
        answerLength: typeof data.answer === 'string' ? data.answer.length : 0,
      });
      if (data.answer) {
        const fullAnswer = data.answer as string;
        setAiAnswer(fullAnswer);

        if (data.rateLimit) {
          const { remaining, resetTimeText } = data.rateLimit;
          if (remaining === 0) {
            message.warning({
              content: `报告生成成功！今日免费次数已用完，${resetTimeText}后恢复。`,
              duration: 6
            });
          } else if (remaining <= 1) {
            message.info({
              content: `报告生成成功！今日还剩 ${remaining} 次免费机会。`,
              duration: 4
            });
          }
        }
      } else if (data.error) {
        throw new RequestError(requestSource, data.error, resp.status, data.details);
      }

      // ========== API 成功后，消耗毒舌版邀请码 ==========
      if (reportStyle === 'toxic') {
        const canProceed = await consumeToxicCode();
        if (!canProceed) {
          console.error('⚠️ 邀请码消耗失败，但报告已生成');
          message.warning('邀请码状态更新失败，请联系客服');
        } else {
          console.log('✅ 毒舌版邀请码消耗成功');
        }
      }

      setIsLocked(false);
      setShowReport(true);
      
      // 生成成功后清空表单草稿
      localStorage.removeItem('bazi_form_draft');
    } catch (error: any) {
      console.error('[bazi] Report request failed', {
        requestId: debugRequestId,
        totalMs: Math.round(performance.now() - requestStartedAt),
      });
      console.error(error);
      const fallbackSource: RequestSource = reportStyle === 'toxic' ? '毒舌版接口' : '温和版接口';
      setReportError('报告生成中断，当前内容不完整，请返回重试。');
      message.error(formatRequestError(error, fallbackSource));
    } finally {
      setAiLoading(false);
      setReportGenerating(false);
    }
  };

  // 重置表单
  const onReset = () => {
    form.resetFields();
    setFourPillars(null);
    setDaYun([]);
    // 清空草稿缓存
    localStorage.removeItem('bazi_form_draft');
  };
  // ========== 渲染 UI ==========
  // 如果在报告页面，渲染报告页
  if (showReport && aiAnswer && fourPillars && astrolabe) {
    return (
      <ConfigProvider locale={zhCN}>
        <Report
          fourPillars={fourPillars}
          daYun={daYun}
          astrolabe={astrolabe}
          aiAnswer={aiAnswer}
          generating={reportGenerating}
          generationError={reportError}
          isLocked={isLocked}
          // reportId={reportId} // 暂时不用
          onBack={() => setShowReport(false)}
          onUnlockSuccess={(fullContent) => {
            // 解锁成功后更新完整内容
            setAiAnswer(fullContent);
            setIsLocked(false);
          }}
        />
        <CustomerServiceFloat />
      </ConfigProvider>
    );
  }

  // 渲染主页面
  return (
    <ConfigProvider locale={zhCN}>
      {/* AI 加载动画 */}
      <AILoadingAnimation visible={aiLoading} />
  
      <div className="app-container">
        {/* 公告栏 */}
        {announcementVisible && import.meta.env.VITE_ANNOUNCEMENT_TEXT && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'linear-gradient(135deg, #f8f6ff 0%, #fff9f0 100%)',
            borderBottom: '2px solid #d4af6a',
            padding: '12px 16px',
            boxShadow: '0 2px 8px rgba(212, 175, 106, 0.2)'
          }}>
            <div style={{
              maxWidth: '900px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div 
                style={{
                  flex: 1,
                  fontSize: '14px',
                  color: '#8b6914',
                  fontWeight: 500,
                  cursor: import.meta.env.VITE_ANNOUNCEMENT_LINK ? 'pointer' : 'default',
                  textAlign: 'center'
                }}
                onClick={() => {
                  const link = import.meta.env.VITE_ANNOUNCEMENT_LINK;
                  if (link) {
                    if (link.startsWith('http')) {
                      window.open(link, '_blank');
                    } else {
                      window.location.href = link;
                    }
                  }
                }}
              >
                {import.meta.env.VITE_ANNOUNCEMENT_TEXT}
              </div>
              <button
                onClick={closeAnnouncement}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#8b6914',
                  fontSize: '18px',
                  lineHeight: 1,
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#d4af6a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#8b6914';
                }}
                aria-label="关闭公告"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        {/* 单栏居中布局 */}
        <div 
          className="app-layout"
          style={{
            paddingTop: announcementVisible && import.meta.env.VITE_ANNOUNCEMENT_TEXT ? '56px' : '0'
          }}
        >
          <div className="center-content">
            <div style={{ width: '100%', maxWidth: '900px' }}>
              {/* 功能选择卡片 */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    fontSize: '22px',
                    fontWeight: 800,
                    color: '#d4af6a',
                    marginBottom: '8px',
                    letterSpacing: '1px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    选择测算类型
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#d4af6a',
                    fontWeight: 500,
                    opacity: 0.9
                  }}>
                    根据你的需求选择适合的分析模式
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  {/* 单人行为模型卡片 */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: '280px',
                      padding: '28px 24px',
                      background: 'linear-gradient(135deg, #f8f6ff 0%, #f0ecff 100%)',
                      borderRadius: '16px',
                      border: '2px solid #8b5cf6',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 30px rgba(139, 92, 246, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => {
                      // 滚动到表单区域
                      document.querySelector('.form-card')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#6b21a8',
                      marginBottom: '8px'
                    }}>
                      单人分析图谱
                    </div>
                    
                    <div style={{
                      fontSize: '14px',
                      color: '#7c3aed',
                      lineHeight: '1.6',
                      marginBottom: '12px'
                    }}>
                      深度解析个人性格底色、人生趋势与关键决策时机
                    </div>

                    {/* 点击提示 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '12px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      borderRadius: '8px',
                      border: '1px dashed rgba(139, 92, 246, 0.3)'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        color: '#6b21a8',
                        fontWeight: 600
                      }}>
                        点击卡片开始测算
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {['行为模型', '关系结构', '阶段趋势', '年度趋势'].map((tag) => (
                        <span key={tag} style={{
                          padding: '4px 10px',
                          background: 'rgba(139, 92, 246, 0.1)',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#7c3aed'
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* 双人关系分析卡片 */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: '280px',
                      padding: '28px 24px',
                      background: 'linear-gradient(135deg, #fff9f0 0%, #fff5e6 100%)',
                      borderRadius: '16px',
                      border: '2px solid #d4af6a',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 30px rgba(212, 175, 106, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => {
                      window.location.href = '/hepan';
                    }}
                  >
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#8b6914',
                      marginBottom: '8px'
                    }}>
                      双人关系分析
                    </div>
                    
                    <div style={{
                      fontSize: '14px',
                      color: '#b8935d',
                      lineHeight: '1.6',
                      marginBottom: '10px'
                    }}>
                      恋人·夫妻·暧昧·商业伙伴·亲子·朋友<br/>
                      多维度解析关系契合度与相处指南
                    </div>

                    {/* 点击提示 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '10px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.15) 0%, rgba(212, 175, 106, 0.05) 100%)',
                      borderRadius: '8px',
                      border: '1px dashed rgba(212, 175, 106, 0.3)'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        color: '#8b6914',
                        fontWeight: 600
                      }}>
                        点击卡片开始测算
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {['契合度评分', '相处模式', '矛盾预警', '优化建议'].map((tag) => (
                        <span key={tag} style={{
                          padding: '4px 10px',
                          background: 'rgba(212, 175, 106, 0.1)',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#b8935d'
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <Link to="/topics" className="home-topic-entry">
                  <div>
                    <div className="home-topic-entry-title">探索更多专题</div>
                    <div className="home-topic-entry-description">
                      印度占星、事业、合盘、孩子天赋，选择你关心的方向深入了解。
                    </div>
                  </div>
                  <span className="home-topic-entry-action">进入专题中心 →</span>
                </Link>

                {isFullMode() && (
                <div className="home-extension-section">
                  <div className="home-extension-header">
                    <div className="home-extension-title">成长与方向专题</div>
                    <div className="home-extension-subtitle">
                      面向家长与个人决策场景，把天赋识别和职业定位单独做深
                    </div>
                  </div>

                  <div className="home-extension-grid">
                    {FREE_SECTIONS.map((section) => (
                      <div
                        key={section.key}
                        className={`home-extension-card ${section.themeClassName}`}
                        onClick={() => {
                          window.location.href = section.href;
                        }}
                      >
                        <div className="home-extension-badge">{section.badge}</div>
                        <div className="home-extension-card-title">{section.title}</div>
                        <div className="home-extension-card-desc">{section.description}</div>
                        <div className="home-extension-tags">
                          {section.tags.map((tag) => (
                            <span key={tag} className="home-extension-tag">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </div>
                
              {/* Hero Section - 宣传文案 */}
              <div className="hero-section">
                <h1 className="hero-title">看清迷雾，不再"摸黑"赶路</h1>
                <h2 className="hero-subtitle">
                  —— 基于 <strong>Gemini</strong>与 <strong>道家师傅实战心法</strong>封装的<strong>Prompt</strong>。
                </h2>
                <p className="hero-description">
                  耗时多天，将 <strong>1000+ 真实案例</strong>背后的因果逻辑转化为 <strong>专业推理逻辑</strong>。
                  这不仅是一份报告，更是一次针对你性格盲点与未来趋势的深度扫描。 <strong>6000+ </strong>字的解析，带你穿透迷雾，看清系统底色，<strong>精准掌握人生主动权</strong>。
                </p>
              
                {/* 核心卖点三连 */}
                <div className="features-grid">
                  <div className="feature-card">
                    <div className="feature-icon expert-icon">
                      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="28" stroke="#d4af6a" strokeWidth="3" fill="rgba(212, 175, 106, 0.1)"/>
                        <path d="M32 12 L38 26 L52 28 L42 38 L44 52 L32 45 L20 52 L22 38 L12 28 L26 26 Z" fill="#d4af6a"/>
                        <circle cx="32" cy="32" r="6" fill="#fff"/>
                      </svg>
                    </div>
                    <h3 className="feature-title">底层逻辑：你的"出厂设置"</h3>
                    <p className="feature-desc">
                      结合<strong>温度、经纬度、天体磁场</strong>，还原你最原始的性格底色。<br/>
                      不预测未来，只<strong>读懂你的说明书</strong>。
                    </p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon data-icon">
                      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="8" y="42" width="12" height="14" fill="#d4af6a" rx="2"/>
                        <rect x="26" y="28" width="12" height="28" fill="#d4af6a" rx="2"/>
                        <rect x="44" y="16" width="12" height="40" fill="#d4af6a" rx="2"/>
                        <path d="M14 38 L32 24 L50 12" stroke="#667eea" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="14" cy="38" r="4" fill="#667eea"/>
                        <circle cx="32" cy="24" r="4" fill="#667eea"/>
                        <circle cx="50" cy="12" r="4" fill="#667eea"/>
                      </svg>
                    </div>
                    <h3 className="feature-title">动态模拟：同一剧本的多重演绎</h3>
                    <p className="feature-desc">
                      基于 <strong>1000+ 真实案例</strong>对比分析。<br/>
                      锁定性格波动区间，助你<strong>活出人生上限</strong>。
                    </p>
                  </div>
                  <div className="feature-card">
                    <div className="feature-icon decision-icon">
                      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M32 8 L56 20 L56 44 L32 56 L8 44 L8 20 Z" stroke="#d4af6a" strokeWidth="3" fill="rgba(212, 175, 106, 0.1)"/>
                        <path d="M32 16 L48 24 L48 40 L32 48 L16 40 L16 24 Z" fill="#d4af6a"/>
                        <circle cx="32" cy="32" r="8" fill="#fff"/>
                        <path d="M28 32 L30 34 L36 28" stroke="#d4af6a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="feature-title">智能导航：你的人生天气预报</h3>
                    <p className="feature-desc">
                      何时<strong>蛰伏蓄力</strong>，何时<strong>全速出击</strong>？<br/>
                      看清路况，让每一次决策都顺势而为。
                    </p>
                  </div>
                </div>
                              
                {/* 核心理念 */}
                <div style={{
                  marginTop: '32px',
                  padding: '20px 24px',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(212, 175, 106, 0.1) 100%)',
                  borderRadius: '12px',
                  border: '2px solid rgba(212, 175, 106, 0.3)',
                  textAlign: 'center'
                }}>
                  <p style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#333',
                    margin: 0,
                    letterSpacing: '1px'
                  }}>
                    🎯 与其被动认命，不如<strong style={{ color: '#d4af6a' }}>主动博弈</strong>
                  </p>
                </div>

                {/* 重要说明卡片 */}
                <div style={{
                  marginTop: '20px',
                  padding: '24px',
                  background: 'linear-gradient(135deg, #fff7e6 0%, #fff1f0 100%)',
                  borderRadius: '14px',
                  border: '2px solid rgba(255, 120, 80, 0.28)',
                  boxShadow: '0 8px 20px rgba(255, 120, 80, 0.12)'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#b54708',
                    marginBottom: '12px',
                    lineHeight: '1.6'
                  }}>
                    重要提醒：看大方向很准，但别拿来押具体结果
                  </div>
                  <div style={{
                    fontSize: '15px',
                    color: '#7a3e1d',
                    lineHeight: '1.95'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      人生说明书极其擅长剖析你的“出厂设置”、帮你看清“搞钱的路径”和“感情的羁绊”。
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      但大家千万别把它当成算具体事情的神仙。底层模型管的是“天气预报”，不管你“明天出门会不会捡到钱”或者“这次考研能不能过”。
                    </div>
                  </div>
                </div>
              </div>

              {/* 表单卡片 */}
              <div className="form-card">
                <h1 className="form-title">人生说明书（单人）</h1>
                <Form
                  form={form}
                  name="bazi-form"
                  onFinish={onFinish}
                  onValuesChange={handleFormChange}
                  layout="vertical"
                  initialValues={{
                    birthDateTime: { date: dayjs(), isLunar: false }
                  }}
                >

            <Form.Item 
              name="birthDateTime" 
              label="出生时间" 
              rules={[{ required: true, message: '请选择出生时间' }]}
            >
              <BirthDateTimePicker />
            </Form.Item>
            
            <Form.Item
              name="gender"
              label="性别"
              rules={[{ required: true, message: '请选择性别' }]}
            >
              <Select
                placeholder="请选择性别"
                options={[
                  { value: 'male', label: '男' },
                  { value: 'female', label: '女' },
                ]}
              />
            </Form.Item>
            
            {/* 用户问题输入 - 毒舌版不支持提问 */}
            {reportStyle !== 'toxic' && (
              <Form.Item
                name="userQuestion"
                label="有什么想问的？（选填，100字以内）"
              >
                <Input.TextArea
                  placeholder="例如：我在什么时候适合转行？"
                  maxLength={100}
                  showCount
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{
                    fontSize: '15px',
                    borderRadius: '8px'
                  }}
                />
              </Form.Item>
            )}
            
            <Form.Item
              name="city"
              label="出生城市"
              rules={[{ required: true, message: '请选择出生城市' }]}
              tooltip="选择城市后自动填充经度"
            >
              <Select
                showSearch
                placeholder="请选择城市"
                options={cityOptions}
                onChange={handleCityChange}
                optionFilterProp="searchText"
                filterOption={cityFilterOption}
              />
            </Form.Item>

            <Form.Item
              name="longitude"
              label="出生地经度"
              rules={[{ required: true, message: '请输入出生地经度' }]}
              tooltip="选择城市后自动填充"
            >
              <Input type="number" placeholder="例如：116.4074" step="0.0001" disabled />
            </Form.Item>

            {/* 早晚子时区分选项 */}
            <Form.Item
              name="distinguishZiShi"
              valuePropName="checked"
              style={{ marginBottom: '24px' }}
            >
              <Checkbox>
                <span style={{ fontSize: '15px', color: '#333', marginRight: '6px' }}>
                  区分早晚子时
                </span>
                <Tooltip
                  title={
                    <div style={{ lineHeight: '1.8', fontSize: '13px' }}>
                      <div style={{ marginBottom: '8px', fontWeight: 600, color: '#ffd666' }}>
                        子时日柱计算规则（真太阳时）：
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <strong>🔲 传统算法（默认）：</strong>
                      </div>
                      <div style={{ marginLeft: '16px', marginBottom: '10px', color: '#d9d9d9' }}>
                        23:00-01:00 统一算第二天
                      </div>
                      <div style={{ marginBottom: '6px' }}>
                        <strong>☑️ 现代算法（勾选后）：</strong>
                      </div>
                      <div style={{ marginLeft: '16px', color: '#d9d9d9' }}>
                        • 23:00-00:00 算当天（早子时）<br/>
                        • 00:00-01:00 算第二天（晚子时）
                      </div>
                    </div>
                  }
                  overlayStyle={{ maxWidth: '320px' }}
                >
                  <QuestionCircleOutlined style={{ color: '#d4af6a', cursor: 'help' }} />
                </Tooltip>
              </Checkbox>
            </Form.Item>

            <Button type="primary" htmlType="submit" className="submit-button">
              开始排盘
            </Button>
            <Button htmlType="button" onClick={onReset} className="reset-button">
              重置
            </Button>
          </Form>
        </div>

        {fourPillars && (
          <div className="result-card">
            <h2 className="result-title">排盘结果</h2>
            <div style={{ textAlign: 'center', marginBottom: 20, color: '#666' }}>
              <p>公历（北京时间）：{form.getFieldValue('birthDateTime')?.date?.format('YYYY年MM月DD日 HH:mm')}</p>
              <p style={{ 
                color: '#d4af6a', 
                fontWeight: 500,
                fontSize: '14px',
                margin: '8px 0'
              }}>
                真太阳时：{trueSolarTimeStr}
              </p>
              <p>农历：{lunarDateStr}</p>
            </div>
            <FourPillars {...fourPillars} />
            
             {/* {ziWeiData && (
              <div className="ziwei-section" style={{ marginTop: 32 }}>
                <h3 className="result-subtitle">关系结构模型分析图谱</h3>
                <ZiWeiChart data={ziWeiData} />
              </div>
            )} */}

            {daYun.length > 0 && (
              <div className="dayun-section">
                <h3 className="result-subtitle">阶段趋势</h3>
                <div className="dayun-list">
                  {daYun.map((item) => (
                    <div key={item.index} className="dayun-item">
                      <span className="dayun-label">第{item.index}运：{item.ganZhi}</span>
                      <span className="dayun-info">({item.startAge} - {item.endAge}岁，{item.startYear}年 - {item.endYear}年)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 风格选择器 */}
            {daYun.length > 0 && (
              <div>
                {/* 标题区域 */}
                <div style={{
                  marginTop: '32px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '8px'
                  }}>
                    🎭 选择报告风格
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#999'
                  }}>
                    不同视角，不同洞察
                  </div>
                </div>

                {/* 风格按钮 */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '20px'
                }}>
                  {/* 温和版 */}
                  <div
                    onClick={() => {
                      setReportStyle('normal');
                      setShowToxicInviteInput(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '20px',
                      background: reportStyle === 'normal' 
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(212, 175, 106, 0.15) 100%)'
                        : 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '16px',
                      border: reportStyle === 'normal'
                        ? '2px solid rgb(212, 175, 106)'
                        : '2px solid rgba(0, 0, 0, 0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (reportStyle !== 'normal') {
                        e.currentTarget.style.borderColor = 'rgba(212, 175, 106, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (reportStyle !== 'normal') {
                        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {reportStyle === 'normal' && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgb(212, 175, 106)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 600
                      }}>
                        ✓
                      </div>
                    )}
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: reportStyle === 'normal' ? 'rgb(212, 175, 106)' : '#666',
                      marginBottom: '8px'
                    }}>
                      ☀️ 温和版
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#999',
                      lineHeight: '1.6'
                    }}>
                      行为模型 + 关系结构双重解析<br/>
                      客观平和，适合初次了解
                    </div>
                  </div>

                  {/* 毒舌版 */}
                  <div
                    onClick={() => {
                      setReportStyle('toxic');
                      if (!isToxicUnlocked) {
                        setShowToxicInviteInput(true);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '20px',
                      background: reportStyle === 'toxic'
                        ? 'linear-gradient(135deg, rgba(139, 69, 69, 0.12) 0%, rgba(212, 175, 106, 0.15) 100%)'
                        : 'rgba(255, 255, 255, 0.5)',
                      borderRadius: '16px',
                      border: reportStyle === 'toxic'
                        ? '2px solid rgb(139, 69, 69)'
                        : '2px solid rgba(0, 0, 0, 0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (reportStyle !== 'toxic') {
                        e.currentTarget.style.borderColor = 'rgba(139, 69, 69, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 69, 69, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (reportStyle !== 'toxic') {
                        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {!isToxicUnlocked && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        fontSize: '20px'
                      }}>
                        🔒
                      </div>
                    )}
                    {reportStyle === 'toxic' && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgb(139, 69, 69)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 600
                      }}>
                        ✓
                      </div>
                    )}
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: reportStyle === 'toxic' ? 'rgb(139, 69, 69)' : '#666',
                      marginBottom: '8px'
                    }}>
                      ⚡ 毒舌版
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#999',
                      lineHeight: '1.6',
                      marginBottom: '8px'
                    }}>
                      <strong style={{ color: '#8b4545' }}>多维度解析·深度剖析</strong>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#999',
                      lineHeight: '1.7',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px'
                    }}>
                      <div>• 行为模型（骨相）：性格底色</div>
                      <div>• 关系结构（皮相）：处世模式</div>
                      <div>• 数据图谱（心相）：精神需求</div>
                    </div>
                  </div>
                </div>
                
                {/* 毒舌版内容区域 - 独占一行 */}
                {(reportStyle === 'toxic' || showToxicInviteInput) && (
                  <div style={{ marginTop: '12px' }}>
                    
                    {/* 毒舌版说明 */}
                    <div style={{ 
                      padding: '16px 20px',
                      background: 'linear-gradient(135deg, rgba(139, 69, 69, 0.06) 0%, rgba(64, 64, 64, 0.04) 100%)',
                      borderRadius: '12px',
                      fontSize: '13px',
                      color: '#666',
                      lineHeight: '1.8',
                      border: '1px solid rgba(139, 69, 69, 0.15)'
                    }}>
                      <div style={{ 
                        fontWeight: 600, 
                        color: '#8b4545', 
                        marginBottom: '10px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ fontSize: '16px' }}>⚠️</span>
                        问祸不问福 · 玻璃心勿入
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <span style={{ color: '#8b4545', fontWeight: 500 }}>•</span>
                          <span>只讲问题不讲好话，真话难听但有用</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <span style={{ color: '#8b4545', fontWeight: 500 }}>•</span>
                          <span>需要邀请码解锁，谨慎选择</span>
                        </div>
                      </div>
                      {isToxicUnlocked && (
                        <div style={{ 
                          marginTop: '12px', 
                          padding: '8px 12px',
                          background: 'rgba(212, 175, 106, 0.1)',
                          borderRadius: '6px',
                          color: '#d4af6a', 
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>✅</span>
                          <span>已解锁 · 剩余 {toxicRemainingUses} 次</span>
                        </div>
                      )}
                    </div>
                    
                    {/* 毒舌版邀请码输入 */}
                    {showToxicInviteInput && !isToxicUnlocked && (
                      <div style={{ 
                        marginTop: '12px',
                        padding: '16px',
                        background: 'linear-gradient(135deg, rgba(139, 69, 69, 0.08) 0%, rgba(212, 175, 106, 0.05) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(139, 69, 69, 0.2)'
                      }}>
                        <div style={{ 
                          fontSize: '15px', 
                          marginBottom: '12px', 
                          color: '#8b4545',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '18px' }}>🔑</span>
                          输入邀请码解锁毒舌版
                        </div>
                        
                        {/* 邀请码输入框 */}
                        <Input
                          placeholder="请输入邀请码"
                          value={toxicInviteCode}
                          onChange={(e) => setToxicInviteCode(e.target.value.toUpperCase())}
                          style={{ 
                            width: '100%',
                            height: '48px',
                            borderRadius: '8px',
                            fontSize: '16px'
                          }}
                          maxLength={20}
                        />
                        
                        {/* 获取邀请码提示 */}
                        <div style={{ 
                          marginTop: '16px',
                          padding: '14px',
                          background: 'rgba(255, 255, 255, 0.6)',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#666',
                          lineHeight: '1.7'
                        }}>
                          <div style={{ marginBottom: '10px', color: '#888', fontSize: '12px' }}>
                            毒舌版融合行为模型+关系结构+数据图谱三重解析，API成本较高，故采用邀请码模式
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <span style={{ fontSize: '14px' }}>💡</span>
                            <span style={{ flex: 1 }}>
                              没有邀请码？
                              <span 
                                onClick={() => setIsServiceModalVisible(true)}
                                style={{ 
                                  color: '#d4af6a', 
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  marginLeft: '4px'
                                }}
                              >
                                联系客服微信
                              </span>
                              <span style={{ marginLeft: '4px' }}>免费获取</span>
                            </span>
                          </div>
                          <div style={{ marginTop: '10px', paddingLeft: '20px', color: '#999', fontSize: '12px' }}>
                            一个邀请码有1次使用机会，如果觉得有帮助欢迎赞赏
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <Button
              className="ai-generate-button"
              onClick={handleAiInterpret}
              loading={aiLoading}
              size="large"
              style={{ marginTop: 16 }}
            >
              <span className="button-text">生成我的人生说明书</span>
            </Button>
            
            {/* 科学背书模块 */}
            <div style={{ 
              marginTop: '24px', 
              padding: '16px',
              background: 'rgba(212, 175, 106, 0.08)',
              borderRadius: '12px',
              fontSize: '13px',
              lineHeight: '1.8',
              color: '#666'
            }}>
              <div style={{ marginBottom: '8px', fontWeight: 600, color: '#d4af6a' }}> 东方关系结构 × 西方心理原型</div>
              <div>• 保留传统关系结构分析对时空规律的精准捕捉</div>
              <div>• 引入荣格人格原型理论进行深度转译</div>
              <div>• 基于 1000+ 真实案例数据验证</div>
            </div>
          </div>
        )}
            </div>
          </div>
        </div>
      </div>
      <CustomerServiceFloat />
      
      {/* 客服微信弹窗 */}
      <Modal
        title="添加小助手微信"
        open={isServiceModalVisible}
        onCancel={() => setIsServiceModalVisible(false)}
        footer={null}
        centered
        width={320}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <img 
            src={weixinQR} 
            alt="微信二维码" 
            style={{ width: '200px', height: '200px', borderRadius: '8px' }} 
          />
          <p style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
            扫描二维码添加小助手微信
          </p>
          <p style={{ marginTop: '8px', color: '#999', fontSize: '13px' }}>
            免费获取毒舌版邀请码
          </p>
        </div>
      </Modal>
    </ConfigProvider>
  )
}

export default App;
